/**
 * AudiobooksSplitView - Split view layout for audiobook list and detail
 *
 * Similar to PodcastsSplitView but tailored for audiobooks
 */
package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.EpisodeImportForm
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AudiobookDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AudiobooksSplitView(
    audiobooks: List<AudiobookDto>,
    loading: Boolean,
    error: String?,
    totalCount: Int,
    currentPage: Int,
    searchQuery: String,
    tags: List<com.castly.shared.dto.TagWithCountDto>,
    selectedTags: MutableSet<String>,
    selectedAudiobook: AudiobookDto?,
    activeTab: AudiobookDetailTab,
    apiClient: ApiClient,
    pageSize: Int = 20,
    onSearchChange: (String) -> Unit,
    onFilterTagsChange: (MutableSet<String>) -> Unit,
    onPageChange: (Int) -> Unit,
    onImportCsv: () -> Unit,
    onSyncReaders: () -> Unit,
    onImportEpisodes: () -> Unit,
    onImportOnix: () -> Unit,
    onIsbnLookup: () -> Unit,
    onImportRss: () -> Unit,
    onCreateAudiobook: () -> Unit = {},
    onRecalculateDurations: () -> Unit = {},
    onAudiobookClick: (AudiobookDto) -> Unit,
    onTabChange: (AudiobookDetailTab) -> Unit,
    onAudiobookUpdate: (String) -> Unit,
    onClose: () -> Unit,
    onRefresh: () -> Unit,
    showDeleted: Boolean = false,
    onToggleShowDeleted: () -> Unit = {},
    onEditAudiobook: (String) -> Unit = {}
) {
    val colors = AppTheme.colors
    var showEpisodeImportDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Div({
        style {
            display(DisplayStyle.Flex)
            height(100.percent)
            gap(Spacing.lg)
            themeTransition()
        }
    }) {
        // Left Panel - Audiobook List
        Div({
            style {
                flex(if (selectedAudiobook != null) "0 0 40%" else "1")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("overflow", "auto")
            }
        }) {
            AudiobookListPanel(
                audiobooks = audiobooks,
                loading = loading,
                error = error,
                totalCount = totalCount,
                currentPage = currentPage,
                pageSize = pageSize,
                searchQuery = searchQuery,
                tags = tags,
                selectedTags = selectedTags,
                selectedAudiobookIsbn = selectedAudiobook?.id,
                apiClient = apiClient,
                onSearchChange = onSearchChange,
                onFilterTagsChange = onFilterTagsChange,
                onPageChange = onPageChange,
                onImportCsv = onImportCsv,
                onSyncReaders = onSyncReaders,
                onImportEpisodes = onImportEpisodes,
                onImportOnix = onImportOnix,
                onIsbnLookup = onIsbnLookup,
                onImportRss = onImportRss,
                onCreateAudiobook = onCreateAudiobook,
                onRecalculateDurations = onRecalculateDurations,
                onAudiobookClick = onAudiobookClick,
                onRefresh = onRefresh,
                showDeleted = showDeleted,
                onToggleShowDeleted = onToggleShowDeleted
            )
        }

        // Right Panel - Audiobook Details
        if (selectedAudiobook != null) {
            Div({
                style {
                    flex("1")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    backgroundColor(Color(colors.surface))
                    borderRadius(BorderRadius.lg)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    property("overflow", "hidden")
                    themeTransition()
                }
            }) {
                AudiobookDetailView(
                    audiobook = selectedAudiobook,
                    activeTab = activeTab,
                    apiClient = apiClient,
                    onTabChange = onTabChange,
                    onAudiobookUpdate = onAudiobookUpdate,
                    onClose = onClose,
                    onShowEpisodeImport = { showEpisodeImportDialog = true },
                    onEdit = { onEditAudiobook(selectedAudiobook.id) }
                )
            }
        }
    }

    // Episode import dialog modal
    if (showEpisodeImportDialog && selectedAudiobook != null) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(600.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                EpisodeImportForm(
                    apiClient = apiClient,
                    isbn = selectedAudiobook.id,
                    onImportComplete = { report ->
                        console.log("Episode import completed", report)
                        // Reload audiobook data to show updated episodes
                        onAudiobookUpdate(selectedAudiobook.id)
                        // Switch to episodes tab
                        onTabChange(AudiobookDetailTab.CHAPTERS)
                    },
                    onCancel = {
                        showEpisodeImportDialog = false
                    }
                )
            }
        }
    }
}

@Composable
private fun AudiobookListPanel(
    audiobooks: List<AudiobookDto>,
    loading: Boolean,
    error: String?,
    totalCount: Int,
    currentPage: Int,
    pageSize: Int,
    searchQuery: String,
    tags: List<com.castly.shared.dto.TagWithCountDto>,
    selectedTags: MutableSet<String>,
    selectedAudiobookIsbn: String?,
    apiClient: ApiClient,
    onSearchChange: (String) -> Unit,
    onFilterTagsChange: (MutableSet<String>) -> Unit,
    onPageChange: (Int) -> Unit,
    onImportCsv: () -> Unit,
    onSyncReaders: () -> Unit,
    onImportEpisodes: () -> Unit,
    onImportOnix: () -> Unit,
    onIsbnLookup: () -> Unit,
    onImportRss: () -> Unit,
    onCreateAudiobook: () -> Unit = {},
    onRecalculateDurations: () -> Unit = {},
    onAudiobookClick: (AudiobookDto) -> Unit,
    onRefresh: () -> Unit,
    showDeleted: Boolean = false,
    onToggleShowDeleted: () -> Unit = {}
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")
    val isSuperAdmin = userRoleStr.uppercase() == "SUPER_ADMIN"
    var showAllTags by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
            height(100.percent)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                }
            }) {
                H1({
                    style {
                        fontSize(Typography.xxl)
                        fontWeight("700")
                        color(Color(colors.text))
                        margin(0.px)
                    }
                }) {
                    Text("Audiobooks")
                }

                OnixExportButton(
                    apiClient = apiClient,
                    searchQuery = searchQuery
                )
            }

            if (canEdit) com.castly.admin.ui.components.IconDropdownButton(
                tooltip = "Audiobook Actions",
                items = buildList {
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "Create Audiobook",
                        description = "Create a new audiobook manually",
                        icon = { com.castly.admin.ui.icons.IconPlus(size = 16) },
                        onClick = onCreateAudiobook
                    ))
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "Import CSV",
                        description = "Import audiobooks from CSV file",
                        icon = { com.castly.admin.ui.icons.IconUpload(size = 16) },
                        onClick = onImportCsv
                    ))
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "Import ONIX",
                        description = "Import from ONIX 2.1/3.0 XML file",
                        icon = { com.castly.admin.ui.icons.IconFileText(size = 16) },
                        onClick = onImportOnix
                    ))
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "Import from RSS",
                        description = "Import audiobook from podcast RSS feed",
                        icon = { com.castly.admin.ui.icons.IconRss(size = 16) },
                        onClick = onImportRss
                    ))
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "ISBN Lookup",
                        description = "Look up metadata by ISBN",
                        icon = { com.castly.admin.ui.icons.IconSearch(size = 16) },
                        onClick = onIsbnLookup
                    ))
                    add(com.castly.admin.ui.components.DropdownMenuItem(
                        label = "Import Episodes",
                        description = "Bulk import episode files",
                        icon = { com.castly.admin.ui.icons.IconUpload(size = 16) },
                        onClick = onImportEpisodes
                    ))
                    if (isSuperAdmin) {
                        add(com.castly.admin.ui.components.DropdownMenuItem(
                            label = "Sync Readers",
                            description = "Sync readers from CSV file",
                            icon = { com.castly.admin.ui.icons.IconRefreshCw(size = 16) },
                            onClick = onSyncReaders
                        ))
                        add(com.castly.admin.ui.components.DropdownMenuItem(
                            label = "Recalculate Durations",
                            description = "Backfill missing episode durations from MP3 files",
                            icon = { com.castly.admin.ui.icons.IconClock(size = 16) },
                            onClick = onRecalculateDurations
                        ))
                    }
                }
            )
        }

        // Tag filter chips
        if (tags.isNotEmpty()) {
            val sortedTags = remember(tags) {
                tags.sortedByDescending { it.audiobookCount }
            }
            val visibleTags = if (showAllTags) sortedTags else sortedTags.take(7)
            val hasMoreTags = sortedTags.size > 7

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    property("flex-wrap", "wrap")
                    alignItems(AlignItems.Center)
                }
            }) {
                // All tags filter
                Button({
                    onClick {
                        val newTags = mutableSetOf<String>()
                        onFilterTagsChange(newTags)
                    }
                    style {
                        padding(6.px, 14.px)
                        backgroundColor(if (selectedTags.isEmpty()) Color(colors.primary) else Color(colors.surfaceHover))
                        color(if (selectedTags.isEmpty()) Color("#ffffff") else Color(colors.text))
                        border(1.px, LineStyle.Solid, if (selectedTags.isEmpty()) Color(colors.primary) else Color(colors.border))
                        borderRadius(16.px)
                        cursor("pointer")
                        fontSize(13.px)
                        fontWeight(if (selectedTags.isEmpty()) "600" else "500")
                        property("transition", "all 0.2s")
                    }
                }) { Text("All Tags") }

                // Tag chips
                visibleTags.forEach { tag ->
                    val isSelected = selectedTags.contains(tag.id)
                    val hasMultipleSelections = selectedTags.size > 1
                    val hasOtherSelections = selectedTags.isNotEmpty() && !isSelected
                    Button({
                        onClick {
                            val newTags = mutableSetOf<String>()
                            if (!isSelected) newTags.add(tag.id)
                            onFilterTagsChange(newTags)
                        }
                        style {
                            padding(6.px, 14.px)
                            backgroundColor(if (isSelected) Color(colors.primary) else Color(colors.surfaceHover))
                            color(if (isSelected) Color("#ffffff") else Color(colors.text))
                            border(1.px, LineStyle.Solid, if (isSelected) Color(colors.primary) else Color(colors.border))
                            borderRadius(16.px)
                            cursor("pointer")
                            fontSize(13.px)
                            fontWeight(if (isSelected) "600" else "500")
                            property("transition", "all 0.2s")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                        }
                    }) {
                        Span { Text("${tag.name} (${tag.audiobookCount})") }
                        // Show "+" to add tag when other tags selected
                        if (hasOtherSelections) {
                            Span({
                                onClick { event ->
                                    event.stopPropagation()
                                    val newTags = selectedTags.toMutableSet()
                                    newTags.add(tag.id)
                                    onFilterTagsChange(newTags)
                                }
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    width(18.px)
                                    height(18.px)
                                    backgroundColor(Color(colors.success))
                                    color(Color("#ffffff"))
                                    borderRadius(9.px)
                                    fontSize(Typography.base)
                                    fontWeight("700")
                                    cursor("pointer")
                                    property("line-height", "1")
                                }
                            }) { Text("+") }
                        }

                        // Show "-" to remove tag when multiple selected
                        if (isSelected && hasMultipleSelections) {
                            Span({
                                onClick { event ->
                                    event.stopPropagation()
                                    val newTags = selectedTags.toMutableSet()
                                    newTags.remove(tag.id)
                                    onFilterTagsChange(newTags)
                                }
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    width(18.px)
                                    height(18.px)
                                    backgroundColor(Color(colors.danger))
                                    color(Color("#ffffff"))
                                    borderRadius(9.px)
                                    fontSize(Typography.base)
                                    fontWeight("700")
                                    cursor("pointer")
                                    property("line-height", "1")
                                }
                            }) { Text("−") }
                        }
                    }
                }

                // See More / Show Less button
                if (hasMoreTags) {
                    Button({
                        onClick { showAllTags = !showAllTags }
                        style {
                            padding(6.px, 14.px)
                            backgroundColor(Color(colors.surfaceHover))
                            color(Color(colors.textMuted))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(16.px)
                            cursor("pointer")
                            fontSize(13.px)
                            fontWeight("500")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        Text(if (showAllTags) "Show Less" else "See More (${sortedTags.size - 7})")
                    }
                }
            }
        }

        // Show deleted toggle
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.sm)
            }
        }) {
            Button({
                onClick { onToggleShowDeleted() }
                style {
                    padding(6.px, 14.px)
                    backgroundColor(if (showDeleted) Color("#f59e0b") else Color(colors.surfaceHover))
                    color(if (showDeleted) Color("#ffffff") else Color(colors.textMuted))
                    border(1.px, LineStyle.Solid, if (showDeleted) Color("#f59e0b") else Color(colors.border))
                    borderRadius(16.px)
                    cursor("pointer")
                    fontSize(13.px)
                    fontWeight(if (showDeleted) "600" else "500")
                    property("transition", "all 0.2s")
                }
            }) {
                Text(if (showDeleted) "Showing hidden" else "Show hidden")
            }
        }

        // Search and count
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                gap(Spacing.lg)
            }
        }) {
            // Search input
            Input(InputType.Text) {
                value(searchQuery)
                onInput { event ->
                    onSearchChange(event.value)
                }
                attr("placeholder", "Search audiobooks...")
                style {
                    flex("1")
                    padding(Spacing.sm, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    themeTransition()
                }
            }

            // Total count
            Span({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                }
            }) {
                Text("$totalCount total")
            }
        }

        // Audiobook list
        Div({
            style {
                flex("1")
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                border(1.px, LineStyle.Solid, Color(colors.border))
                property("overflow", "auto")
                themeTransition()
            }
        }) {
            if (loading) {
                Div({
                    style {
                        textAlign("center")
                        padding(40.px)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Loading audiobooks...")
                }
            } else if (error != null) {
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        margin(Spacing.lg)
                        themeTransition()
                    }
                }) {
                    Text(error)
                }
            } else if (audiobooks.isEmpty()) {
                Div({
                    style {
                        textAlign("center")
                        padding(40.px)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text(if (searchQuery.isNotBlank()) "No audiobooks found" else "No audiobooks yet. Import from CSV to get started!")
                }
            } else {
                audiobooks.forEach { audiobook ->
                    AudiobookCard(
                        audiobook = audiobook,
                        isSelected = audiobook.id == selectedAudiobookIsbn,
                        onClick = { onAudiobookClick(audiobook) }
                    )
                }
            }
        }

        // Pagination
        val totalPages = (totalCount + pageSize - 1) / pageSize
        if (totalPages > 1) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    gap(Spacing.sm)
                }
            }) {
                Button({
                    if (currentPage <= 1) {
                        attr("disabled", "true")
                    }
                    onClick {
                        if (currentPage > 1) {
                            onPageChange(currentPage - 1)
                        }
                    }
                    style {
                        padding(6.px, Spacing.md)
                        fontSize(Typography.base)
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor(if (currentPage <= 1) "not-allowed" else "pointer")
                        color(Color(colors.text))
                        themeTransition()
                    }
                }) {
                    Text("←")
                }

                Span({
                    style {
                        padding(6.px, Spacing.md)
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Page $currentPage of $totalPages")
                }

                Button({
                    if (currentPage >= totalPages) {
                        attr("disabled", "true")
                    }
                    onClick {
                        if (currentPage < totalPages) {
                            onPageChange(currentPage + 1)
                        }
                    }
                    style {
                        padding(6.px, Spacing.md)
                        fontSize(Typography.base)
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor(if (currentPage >= totalPages) "not-allowed" else "pointer")
                        color(Color(colors.text))
                        themeTransition()
                    }
                }) {
                    Text("→")
                }
            }
        }
    }

}

@Composable
private fun AudiobookCard(
    audiobook: AudiobookDto,
    isSelected: Boolean = false,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val isHidden = audiobook.deletedAt != null
    Div({
        onClick { onClick() }
        style {
            padding(Spacing.lg)
            property("border-bottom", "1px solid ${colors.border}")
            cursor("pointer")
            property("transition", "background-color 0.2s")
            if (isSelected) {
                backgroundColor(Color(if (colors == LightColors) "#f0f9ff" else "#0c1929"))
            }
            if (isHidden) opacity(0.5)
            display(DisplayStyle.Flex)
            gap(Spacing.md)
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
        onMouseOut {
            if (!isSelected) {
                it.target.asDynamic().style.backgroundColor = "transparent"
            }
        }
    }) {
        // Cover art thumbnail
        val displayImageUrl = audiobook.coverImageUrl

        displayImageUrl?.let { imageUrl ->
            Img(imageUrl) {
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(BorderRadius.md)
                    property("object-fit", "cover")
                    flexShrink(0)
                }
            }
        } ?: run {
            Div({
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color(colors.border))
                    borderRadius(BorderRadius.md)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(10.px)
                    color(Color(colors.textMuted))
                    flexShrink(0)
                }
            }) { Text("No Art") }
        }

        // Content container
        Div({
            style {
                flex("1")
            }
        }) {
            // Title
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.lg)
                    color(Color(colors.text))
                    marginBottom(Spacing.xs)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                }
            }) {
                Text(audiobook.title)
                if (audiobook.status == "DRAFT") {
                    Span({
                        style {
                            fontSize(Typography.xs)
                            padding(2.px, 6.px)
                            backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#422006"))
                            color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                            borderRadius(BorderRadius.sm)
                            fontWeight("600")
                        }
                    }) { Text("Draft") }
                }
                if (isHidden) {
                    Span({
                        style {
                            fontSize(Typography.xs)
                            padding(2.px, 6.px)
                            backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#422006"))
                            color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                            borderRadius(BorderRadius.sm)
                            fontWeight("600")
                        }
                    }) { Text("Hidden") }
                }
            }

            // Author, Reader, and Episode Count
            Div({
                style {
                    fontSize(13.px)
                    color(Color(colors.textMuted))
                    marginBottom(6.px)
                }
            }) {
                Span { Text(audiobook.authorName ?: "Unknown Author") }
                audiobook.readerName?.let { reader ->
                    Span { Text(" • ") }
                    Span { Text(reader) }
                }
                Span({
                    style {
                        property("unicode-bidi", "isolate")
                        property("direction", "ltr")
                    }
                }) { Text(" • ${audiobook.episodeCount} episodes") }
            }

            // Tags (first 3)
            audiobook.tags?.take(3)?.let { tags ->
                if (tags.isNotEmpty()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(6.px)
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        tags.forEach { tag ->
                            Span({
                                style {
                                    fontSize(Typography.xs)
                                    padding(3.px, Spacing.sm)
                                    backgroundColor(Color(if (colors == LightColors) "#eff6ff" else "#0c1929"))
                                    color(Color(colors.primary))
                                    borderRadius(BorderRadius.sm)
                                    fontWeight("500")
                                }
                            }) {
                                Text(tag)
                            }
                        }
                    }
                }
            }
        }
    }
}
